<template>
  <div class="container-xl mt-1 mb-5">
    <div class="h-100 d-flex flex-column">
      <div
        class="observation-details-header flex-shrink-1 d-flex flex-wrap bg-light border-bottom px-3 py-2"
      >
        <h2 class="ml-0 mt-0 mb-0">Edit Observation</h2>

        <div class="mr-3">
          <b-tooltip target="observationSaveButtons" triggers="hover">
            <div>Updated: {{ this.moment(vuexObservation.updatedAt) }}</div>
            <div>Created: {{ this.moment(vuexObservation.createdAt) }}</div>
          </b-tooltip>
          <div id="observationSaveButtons" style="cursor: pointer">
            <span> Last Saved: </span>
            <FromNow
              :dateTime="vuexObservation.updatedAt"
              class="font-weight-bold"
            ></FromNow>
            <i class="fa fa-question-circle text-secondary"></i>
            <button
              v-if="!hasChanges"
              class="btn btn-success py-1 ml-3"
              disabled
            >
              All Changes Saved
            </button>
            <button
              v-else-if="true"
              class="btn btn-success py-1 ml-3"
              @click="onSaveObservationClick()"
            >
              <i class="fa fa-save mr-2"></i>Save Changes
            </button>
            <button v-else class="btn btn-primary py-1 ml-3" disabled>
              <i class="fa fa-spin fa-circle-notch mr-2"></i>Saving
            </button>
            <router-link
              v-if="hasChanges"
              class="btn btn-warning py-1 ml-3"
              alt="Cancel editing this observation"
              title="Cancel editing this observation"
              :to="{
                name: 'ObservationShow',
                params: { id: this.$route.params.id },
              }"
            >
              <i class="fa fa-warning"></i>
              Cancel
            </router-link>
            <router-link
              v-if="!hasChanges"
              class="btn btn-primary py-1 ml-3"
              alt="Cancel editing this observation"
              title="Cancel editing this observation"
              :to="{
                name: 'ObservationShow',
                params: { id: this.$route.params.id },
              }"
            >
              Back to Observation
            </router-link>
          </div>
        </div>

        <div class="text-right py-1">
          <router-link
            v-if="vuexObservation && vuexObservation.project"
            :to="{
              name: 'ProjectShow',
              params: { id: vuexObservation.project.urlField },
            }"
          >
            <i class="fa fa-arrow-right mr-1"></i>To
            {{ vuexObservation.project.name }}
          </router-link>
        </div>
      </div>

      <div
        class="observation-edit-body h-100 bg-light"
        style="overflow-y: auto"
      >
        <div class="row no-gutters pb-5">
          <div
            v-if="vuexObservation.datasheet"
            class="col-12 h3 font-weight-bold bg-cit-green text-white text-center py-2 mb-0 border border-cit-green-light"
          >
            {{ vuexObservation.datasheet.name }}
            <router-link
              class="btn btn-tertiary d-none"
              alt="Edit Datasheet"
              title="Edit Datasheet"
              :to="{
                name: 'DatasheetCreator',
                params: { id: vuexObservation.datasheet.id },
              }"
            >
              <i class="fa fa-edit"></i>
            </router-link>
          </div>
          <div
            v-if="!isLoadingObservation && !vuexObservation.datasheet"
            class="alert alert-danger col-12 text-center py-2 mb-0"
          >
            The datasheet used to create this observation was deleted
          </div>

          <div
            v-if="
              !isLoadingObservation &&
              vuexObservation.datasheet &&
              !vuexObservation.datasheet.published
            "
            class="alert alert-warning col-12 text-center py-2 mb-0"
          >
            This datasheet is not currently published. You may still edit this
            observation.
          </div>
          <VueLoader
            v-if="isLoadingObservation"
            class="my-5 py-5"
            text="Loading Observation"
          />
          <b-alert
            :show="error.length > 0"
            variant="danger"
            dismissible
            @dissmissed="onDismissedClick"
          >
            {{ error }}
          </b-alert>
          <b-container class="mt-3" v-if="!isLoadingObservation">
            <RichText
              :unsafeHTML="vuexObservation.datasheet.instructions"
              v-if="
                vuexObservation.datasheet &&
                vuexObservation.datasheet.instructions
              "
            >
            </RichText>

            <div class="row mb-3">
              <label class="col-md-3 font-weight-bold text-muted">
                Observer <span class="text-danger">&#42;</span>
              </label>
              <div class="col-md-9 date-type-picker-wrapper">
                <b-form-input
                  id="observer"
                  v-model="vuexObservation.user.displayName"
                  type="text"
                  readonly
                  disabled
                ></b-form-input>
              </div>
            </div>

            <div
              id="observationDurations"
              class="row"
              v-if="
                travelDurationFormat !== 'disabled' ||
                setupDurationFormat !== 'disabled'
              "
            >
              <div
                :class="[
                  setupDurationFormat === 'disabled' ? 'col-md-12' : 'col-md-6',
                ]"
                v-if="travelDurationFormat !== 'disabled'"
              >
                <ObservationDurationTravel
                  :format="travelDurationFormat"
                  :description="travelDurationDescription"
                  v-model="durationTravel"
                />
              </div>
              <div
                :class="[
                  travelDurationFormat === 'disabled'
                    ? 'col-md-12'
                    : 'col-md-6',
                ]"
                v-if="setupDurationFormat !== 'disabled'"
              >
                <ObservationDurationSetup
                  :format="setupDurationFormat"
                  :description="setupDurationDescription"
                  v-model="durationSetup"
                />
              </div>
            </div>
            <ObservationDateTime
              :type="'header'"
              :dateFormat="observationDateFormat"
              :observationStart="observedAt"
              :observationEnd="observationEndedAt"
              :durationUnit="durationUnit"
              @update:start="onObservationStartUpdated($event)"
              @update:end="onObservationEndUpdated($event)"
              @change-duration-unit="toggleDurationUnit"
            />

            <ObservationLocationEditor
              v-if="vuexObservation.location && vuexObservation.datasheet"
              ref="location"
              v-model="location"
              :locationOriginal="vuexObservation.location"
              :locationFormat="vuexObservation.datasheet.locationFormat"
              :datasheetId="vuexObservation.datasheet['@id']"
              :projectId="vuexObservation.project['@id']"
              :projectUrlField="vuexObservation.project.urlField"
              :isManager="this.isProjectRoleManager(this.vuexObservation.project.id)"
            ></ObservationLocationEditor>
          </b-container>
          <b-container v-if="!isLoadingObservation">
            <RecordList
              :records="newObservationDatasheetRecords || newObservationRecords"
            ></RecordList>
          </b-container>
        </div>
      </div>

      <b-modal
        v-if="vuexObservation"
        ref="leaveModal"
        id="leaveModal"
        size="lg"
        centered
        title="Unsaved Changes"
        ok-variant="primary"
        ok-title="Discard and Leave"
        cancel-title="Cancel"
      >
        <p>You have modified this observation & have unsaved changes.</p>
        <p>It was last saved {{ this.moment(vuexObservation.updatedAt) }}</p>
      </b-modal>
    </div>
  </div>
</template>

<script>
import VueLoader from "../../layout/Loader";
import ObservationLocationEditor from "./location/index.vue";
import moment from "moment";
import { mapActions, mapGetters } from "vuex";
import RecordList from "./RecordList";
import ObservationDateTime from "../../datasheet/ObservationDateTimeHTML5.vue";
import ObservationDurationTravel from "../../datasheet/ObservationDurationTravel.vue";
import ObservationDurationSetup from "../../datasheet/ObservationDurationSetup.vue";
import FromNow from "../../layout/FromNow";
import RichText from "../../layout/RichText.vue";


export default {
  components: {
    VueLoader,
    ObservationLocationEditor,
    RichText,
    ObservationDateTime,
    ObservationDurationTravel,
    ObservationDurationSetup,
    RecordList,
    FromNow,
  },

  provide() {
    return { parentValidator: null };
  },

  data() {
    return {
      hasChanges: false,
      observedAt: null,
      observationEndedAt: null,
      durationTravel: null,
      durationSetup: null,
      durationUnit: "minutes",

      newObservationRecords: [],
      newObservationDatasheetRecords: null,

      datetimePickerOptions: {
        allowInputToggle: true,
        format: "YYYY/MM/DD h:mm:ss A",
        showClose: true,
        useStrict: true,
        maxDate: moment().add(30, "m").toDate(),
      },

      location: {
        latitude: null,
        longitude: null,
        name: null,
        '@id': null,
      },
      parentValidator: null,
      editDateValue: "",

      textAreaValue: "",
      dateValue: "",
    };
  },

  computed: {
    ...mapGetters({
      isLoadingObservation: "observation/show/isLoading",
      isLoadingDatasheet: "datasheet/creator/retrievedDatasheetIsLoading",
      vuexObservation: "observation/show/retrieved",
      vuexDatasheet: "datasheet/creator/retrievedDatasheet",

      error: "observation/show/error",
      deletedSuccess: "observation/show/deletedSuccess",
    }),
    canEdit() {
      if (!this.vuexObservation || !this.vuexObservation.project) {
        return false;
      }
      return (
        this.isProjectRoleManager(this.vuexObservation.project.id) ||
        this.isObserver(this.vuexObservation)
      );
    },
    observationDateFormat() {
      if (this.vuexDatasheet && this.vuexDatasheet.dateFormat) {
        return this.vuexDatasheet.dateFormat;
      }
      if (
        this.vuexObservation &&
        this.vuexObservation.datasheet &&
        this.vuexObservation.datasheet.dateFormat
      ) {
        return this.vuexObservation.datasheet.dateFormat;
      }
      return "start_end_minutes";
    },
    supportsObservationEnd() {
      return ["start_end_minutes", "start_end_seconds"].includes(
        this.observationDateFormat,
      );
    },
    travelDurationFormat() {
      if (this.vuexDatasheet && this.vuexDatasheet.travelDurationFormat) {
        return this.vuexDatasheet.travelDurationFormat;
      }
      return "disabled";
    },
    setupDurationFormat() {
      if (this.vuexDatasheet && this.vuexDatasheet.setupDurationFormat) {
        return this.vuexDatasheet.setupDurationFormat;
      }
      return "disabled";
    },
    travelDurationDescription() {
      return this.vuexDatasheet && this.vuexDatasheet.travelDurationDescription
        ? this.vuexDatasheet.travelDurationDescription
        : null;
    },
    setupDurationDescription() {
      return this.vuexDatasheet && this.vuexDatasheet.setupDurationDescription
        ? this.vuexDatasheet.setupDurationDescription
        : null;
    },
  },

  watch: {
    /**
     * @function vuexObservation - function that is called when this.observation is changed
     * @param vuexObservation
     */
    vuexObservation(vuexObservation) {
      this.$nextTick(() => {
        this.hasChanges = false;
      });

      this.observedAt = vuexObservation.observedAt
        ? moment(vuexObservation.observedAt.split("+")[0])
        : null;
      this.observationEndedAt = vuexObservation.observationEndedAt
        ? moment(vuexObservation.observationEndedAt.split("+")[0])
        : null;
      this.durationTravel = vuexObservation.durationTravel;
      this.durationSetup = vuexObservation.durationSetup;

      this.location = {...vuexObservation.location};


      // deep copy records so modifying them doesn't affect vuex
      this.newObservationRecords = this.setUsefulRecordFields(
        vuexObservation.records,
      );
      if (vuexObservation.datasheet) {
        this.getDatasheet(this.vuexObservation.datasheet["@id"]);
      } else {
        // observation does not have a datasheet
        // This occurs with some 1.0 data & is OK
        // ToDo - Be sure this case is handled appropriately
      }
    },
    vuexDatasheet(vuexDatasheet) {
      if (!vuexDatasheet) {
        return;
      } else if (vuexDatasheet.status === "draft") {
        this.customErrorMessage =
          "This datasheet must be published to edit this observation.";
      }
    },
    newObservationRecords: {
      deep: true,
      handler: function () {
        this.hasChanges = true;
      },
    },
    observedAt: {
      handler: function () {
        this.hasChanges = true;
      },
    },
    observationEndedAt: {
      handler: function () {
        this.hasChanges = true;
      },
    },
    durationTravel: {
      handler: function () {
        this.hasChanges = true;
      },
    },
    durationSetup: {
      handler: function () {
        this.hasChanges = true;
      },
    },
    location: {
      deep: true,
      handler: function () {
        this.hasChanges = true;
      },
    },
  },

  methods: {
    ...mapActions({
      getObservation: "observation/show/getObservation",
      getDatasheet: "datasheet/creator/retrieveDatasheet",

      resetError: "observation/show/resetError",
      setError: "observation/show/setError",
      uploadFile: "observation/show/uploadObservationImage",
      createRecord: "observation/show/createRecord",
      observationUpdate: "observation/show/observationUpdate",
      mainObservationDetailsUpdate:
        "observation/show/mainObservationDetailsUpdate",
    }),
    moment(value) {
      return moment(value);
    },
    setUsefulRecordFields(records) {
      // This method sets the datasheet records
      if (!records || records.length === 0) {
        return [];
      }
      return records.map((record) => {
        // use marker as identifier for modifying records, default to @ID
        // set as random UUID for new records until they receive an ID

        const value =
          record.recordType === "datetime"
            ? moment(record.value.split("+")[0]).format(
                this.datetimePickerOptions.format,
              )
            : record.value;

        // reformat datasheet record if applicable
        let datasheetRecord = null;
        if (record.datasheetRecord) {
          datasheetRecord = { ...record.datasheetRecord };
          if (
            ["dropdown", "radio"].includes(record.datasheetRecord.recordType)
          ) {
            datasheetRecord.optionValues =
              datasheetRecord.optionsSet.optionsValues.map((optionValue) => {
                return { text: optionValue.value, value: optionValue["@id"] };
              });
          }
          if (["multiple_choice"].includes(record.datasheetRecord.recordType)) {
            datasheetRecord.optionValues =
              datasheetRecord.optionsSet.optionsValues.map((optionValue) => {
                return { text: optionValue.value, value: optionValue["@id"] };
              });
          }
        }

        return {
          ...record,
          datasheetRecord: datasheetRecord,
          value: value,
          marker: record.id.toString(),
          records: this.setUsefulRecordFields(record.records),
        };
      });
    },

    stripRecords(records) {
      // This method is for submitting updates & removes fields & changes options to iri
      if (!records || records.length === 0) {
        return [];
      }
      let recordsSub = [];
      const recordsTop = records.map((record, index) => {
        let newRecord = {
          orderNumber: index,
        };
        if (record.optionValue) {
          newRecord.optionValue = record.optionValue["@id"];
        }
        if (record.multiSelectOptionValues) {
          if (Array.isArray(record.multiSelectOptionValues)) {
            newRecord.multiSelectOptionValues = record.multiSelectOptionValues;
          } else {
            newRecord.multiSelectOptionValues =
              record.multiSelectOptionValues.map((val) => val["id"]);
          }
        }
        if (record.datasheetRecord) {
          newRecord.datasheetRecord = record.datasheetRecord["@id"];
        }
        if (record.fileObject) {
          newRecord.fileObject = record.fileObject["@id"];
        }
        if (record.organism) {
          newRecord.organism = record.organism["@id"];
          recordsSub = [...recordsSub, ...this.stripRecords(record.records)];
        }
        return {
          ...newRecord,
          value: record.value,
          recordType: record.recordType,
          id: record.id,
          ["@id"]: record["@id"],
        };
      });
      return [...recordsTop, ...recordsSub];
    },

    /**
     * @function onSaveClick
     */
    onSaveObservationClick() {
      const observedAtMoment = this.observedAt
        ? moment.isMoment(this.observedAt)
          ? this.observedAt
          : moment(this.observedAt)
        : null;
      const observationEndedAtMoment = this.observationEndedAt
        ? moment.isMoment(this.observationEndedAt)
          ? this.observationEndedAt
          : moment(this.observationEndedAt)
        : null;

      let data = {
        observedAt:
          observedAtMoment && observedAtMoment.isValid()
            ? observedAtMoment.format("YYYY-MM-DDTHH:mm:ss")
            : null,
        ...(this.supportsObservationEnd
          ? {
              observationEndedAt:
                observationEndedAtMoment && observationEndedAtMoment.isValid()
                  ? observationEndedAtMoment.format("YYYY-MM-DDTHH:mm:ss")
                  : null,
            }
          : {}),
        durationTravel: this.durationTravel,
        durationSetup: this.durationSetup,
        location: this.location['@id'],
        lngLat: this.location.lngLat,
        records: this.stripRecords(this.newObservationRecords),
      };
      this.observationUpdate({
        id: this.vuexObservation.id,
        observation: data,
      });
    },

    /**
     * @function onDismissedClick - when the user dismiss error alert, we reset error message
     */
    onDismissedClick() {
      this.resetError();
    },
    onObservationStartUpdated(value) {
      this.observedAt = value ? moment(value) : null;
    },
    onObservationEndUpdated(value) {
      this.observationEndedAt = value ? moment(value) : null;
    },
    onTravelDurationUpdated(value) {
      this.durationTravel = value;
    },
    onSetupDurationUpdated(value) {
      this.durationSetup = value;
    },
    toggleDurationUnit() {
      this.durationUnit = this.durationUnit === "minutes" ? "hours" : "minutes";
    },
  },

  created() {
    this.resetError();
    this.getObservation({
      id: this.$route.params.id,
      query: "?context=update",
    }).then((data) => {
      if (!data) {
        this.$router.push({ name: "NotFound" });
      }
    });
  },
  beforeRouteLeave(to, from, next) {
    // Notify user they have unsaved changes
    if (this.hasChanges) {
      this.$bvModal.show("leaveModal");
      this.$nextTick(() => {
        // Set up hide handler.
        // Done in next tick to ensure Modal is in DOM
        this.$refs.leaveModal.$once("hide", (bvEvt) => {
          if (bvEvt.trigger === "ok") {
            // User clicked on built in OK button
            // So we set up a handler to listen for modal 'hidden' event
            // and advance to next route once hidden
            this.$refs.leaveModal.$once("hidden", () => {
              next();
            });
          } else {
            // User clicked cancel or the modal 'x' close button
            next(false);
          }
        });
      });
    } else {
      next();
    }
  },
};
</script>
